import React, { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Loader from "pages/loader/Loader";
import Refresh from "pages/refresh/Refresh";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";
import MaxuLogo from "assets/images/nav-logo.png";
import ContentPlayerDialog from "examples/Dialog/ContentPlayerDialog";

const ViewActivity = () => {
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const { t } = useTranslation();

  const [learnersActivity, setLearnersActivity] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [userMetricsData, setUserMetricsData] = useState(null);
  const [likedContent, setLikedContent] = useState([]);
  const [dislikedContent, setDislikedContent] = useState([]);
  const [savedContent, setSavedContent] = useState([]);
  const [viewedContent, setViewedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noContent, setNoContent] = useState(false);
  const [content, setContent] = useState({});
  const [playContent, setPlayContent] = useState(false);
  const [hasChildren, setHasChildren] = useState(false);

  useEffect(() => {
    if (profile?.id) {
      getLearnersActivity();
      loadUserContentMetrics();
      hasChildrenHandler();
    }
  }, [profile]);

  const hasChildrenHandler = () => {
    if (
      profile?.relationships?.children?.length > 0 ||
      profile?.relationships?.athletes?.length > 0 || profile?.type?.includes("Shared")
    ) {
      setHasChildren(true);
    }
  };

  const getLearnersActivity = () => {
    call(`users/${profile.id}/learnersactivity`, "GET", {})
      .then((response) => {
        if (response.status === 200) {
          const { activity = [], start_date, end_date } = response.data;
          if (activity.length === 0) {
            setNoContent(true);
          } else {
            setLearnersActivity(activity);
            setStartDate(start_date);
            setEndDate(end_date);
          }
        } else if (response.status === 204) {
          setNoContent(true);
        } else if (response.status === 440) {
          setSessionExpired(true);
        }
      })
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
        setNoContent(true);
      })
      .finally(() => setLoading(false));
  };

  const loadUserContentMetrics = () => {
    call(`users/${profile.id}/contentmetrics`, "GET", {})
      .then((res) => {
        if (res.status === 200) {
          const data = res.data;
          setUserMetricsData(data);
          setLikedContent(data.likedcontent || []);
          setDislikedContent(data.dislikedcontent || []);
          setViewedContent(data.viewedcontent || []);
          setSavedContent(data.savedcontent || []);
        } else if (res.status === 440) {
          setSessionExpired(true);
        }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const getContentLinkHandler = (item) => {
    call(`content/${item.title}`, "GET", {})
      .then((res) => {
        if (res.status === 200) {
          setPlayContent(true);
          setContent(res.data);
        } else if (res.status === 440) {
          setSessionExpired(true);
        }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const closeContentPlayer = () => setPlayContent(false);

  // --- Like / Dislike / Save Handlers ---
  const updateMetrics = (metricData) =>
    call(`users/${profile.id}/contentmetrics`, "PUT", { body: metricData })
      .then((res) => {
        if (res.status === 200) {
          setUserMetricsData(metricData);
        } else if (res.status === 440) {
          setSessionExpired(true);
        }
      })
      .catch((err) => Sentry.captureException(err));

  const likeHandler = (title) => {
    const currentLiked = [...likedContent];
    const currentDisliked = [...dislikedContent];
    const eventkey = { event: "likecontent", value: title };

    if (!currentLiked.includes(title)) currentLiked.push(title);
    const dislikedIndex = currentDisliked.indexOf(title);
    if (dislikedIndex > -1) currentDisliked.splice(dislikedIndex, 1);

    const metricData = {
      ...userMetricsData,
      likedcontent: currentLiked,
      dislikedcontent: currentDisliked,
      eventkey,
    };

    updateMetrics(metricData);
    setLikedContent(currentLiked);
    setDislikedContent(currentDisliked);
  };

  const dislikeHandler = (title) => {
    const currentLiked = [...likedContent];
    const currentDisliked = [...dislikedContent];
    const eventkey = { event: "dislikecontent", value: title };

    if (!currentDisliked.includes(title)) currentDisliked.push(title);
    const likedIndex = currentLiked.indexOf(title);
    if (likedIndex > -1) currentLiked.splice(likedIndex, 1);

    const metricData = {
      ...userMetricsData,
      likedcontent: currentLiked,
      dislikedcontent: currentDisliked,
      eventkey,
    };

    updateMetrics(metricData);
    setLikedContent(currentLiked);
    setDislikedContent(currentDisliked);
  };

  const saveHandler = (title) => {
    const currentSaved = [...savedContent];
    const alreadySaved = currentSaved.includes(title);
    const eventkey = {
      event: alreadySaved ? "removeSaved" : "savedcontent",
      value: title,
    };

    if (alreadySaved) {
      const index = currentSaved.indexOf(title);
      currentSaved.splice(index, 1);
    } else {
      currentSaved.push(title);
    }

    const metricData = {
      ...userMetricsData,
      savedcontent: currentSaved,
      eventkey,
    };

    updateMetrics(metricData);
    setSavedContent(currentSaved);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.viewActivity")} />

      {/* Loading Dialog */}
      {loading && (
        <Dialog open={loading} fullScreen>
          <DialogContent>
            <Loader loaderText={t("loaderText.loadingActivity")} />
          </DialogContent>
        </Dialog>
      )}

      {/* Session Expired */}
      {sessionExpired && <Refresh />}

      {/* No Content */}
      {noContent && !loading && (
        <MDBox mt={5}>
          <Card style={{ backgroundColor: "#10b0d5", padding: 20, opacity: 0.8 }}>
            <MDTypography
              textAlign="center"
              justifyContent="center"
              component="h3"
              fontWeight="medium"
              color="white"
              flexWrap="wrap"
            >
              {hasChildren
                ? t("activityReport.noActivityAvailable")
                : t("activityReport.noChildren")}
            </MDTypography>

            <MDTypography
              textAlign="center"
              component="h3"
              variant="body2"
              fontWeight="medium"
              color="white"
              alignItems="center"
              flexWrap="wrap"
            >
              {t("playList.contactSupport")} <a href="mailto:support@maxu.co">support@maxu.co</a>
            </MDTypography>
          </Card>
        </MDBox>
      )}

      {/* Learners Activity */}
      {!noContent && learnersActivity.length > 0 && (
        <Card
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            p: 4,
            backgroundColor: "white",
            mt: 2,
          }}
        >
          <MDBox textAlign="center" mb={3}>
            <MDTypography variant="h4" color="primary" fontWeight="bold">
              {t("activityReport.title")}
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary">
              {t("activityReport.dateRange", { startDate, endDate })}
            </MDTypography>
          </MDBox>

          {learnersActivity.map((item, index) => (
            <MDBox
              key={index}
              border="1px solid #e5e7eb"
              borderRadius={2}
              p={3}
              mb={4}
              sx={{
                transition: "box-shadow 0.3s",
                "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.1)" },
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <MDBox display="flex" justifyContent="center">
                    <MDBox
                      component="img"
                      src={item.learner_animal || MaxuLogo}
                      alt={t("imageAlts.maxuLogo")}
                      width="100%"
                      maxWidth="180px"
                      borderRadius="12px"
                      boxShadow="0 2px 6px rgba(0,0,0,0.1)"
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12} sm={9}>
                  <MDTypography variant="h5" fontWeight="bold" color="text.primary">
                    {item.name}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={1}>
                    {t("activityReport.sinceDate", { startDate, name: item.name.split(" ")[0] })}
                  </MDTypography>

                  <ul style={{ margin: 0, paddingLeft: 20, color: "#374151" }}>
                    <li>
                      <strong>{item.content_count}</strong> {t("activityReport.newVideosViewed")}
                    </li>
                    <li>
                      <strong>{item.login_count}</strong> {t("activityReport.logins")}
                    </li>
                  </ul>

                  {item.watched_content.length > 0 ? (
                    <MDTypography variant="subtitle2" color="text.primary" fontWeight="bold" mt={2}>
                      {t("activityReport.watchedContent")}
                    </MDTypography>
                  ) : (
                    <MDTypography variant="body2" color="text.disabled" mt={1}>
                      {t("activityReport.noContent")}
                    </MDTypography>
                  )}
                </Grid>
              </Grid>

              {/* Watched Content Grid */}
              {item.watched_content.length > 0 && (
                <Grid container spacing={3} mt={1}>
                  {item.watched_content.map((content, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <DefaultProjectCard
                        item={content}
                        title={content.title}
                        type={content.content_type}
                        image={content.thumbnail_url || MaxuLogo}
                        liked={likedContent.includes(content.title)}
                        disliked={dislikedContent.includes(content.title)}
                        saved={savedContent.includes(content.title)}
                        viewed={viewedContent.includes(content.title)}
                        getContentLinkHandler={getContentLinkHandler}
                        likeHandler={likeHandler}
                        dislikeHandler={dislikeHandler}
                        saveHandler={saveHandler}
                        action={{
                          type: "external",
                          route: content.content_url,
                          color: "info",
                          label: t("buttonTitles.view"),
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </MDBox>
          ))}
        </Card>
      )}

      {playContent && (
        <ContentPlayerDialog
          playContent={playContent}
          closeContentPlayer={closeContentPlayer}
          content={content}
          userMetricsData={userMetricsData}
          setUserMetricData={setUserMetricsData}
          setViewedContent={setViewedContent}
          userId={profile.id}
          setSessionExpired={setSessionExpired}
          t={t}
        />
      )}
    </DashboardLayout>
  );
};

export default ViewActivity;
